import type { Prisma } from "@prisma/client";
import { uniq } from "lodash";
import type {
  BuyableItemEvent,
  BuyableItemHandler,
  PartialBuyableItem,
} from "../buyable-item.types";
import {
  EQUIPMENT_DELIVERY_FEE_GROUP_KEY_PREFIX,
  EVENT_EQUIPMENT_REQUEST_GROUP_KEY_PREFIX as GROUP_KEY_PREFIX,
} from "./equipment-group-key-prefixes";

type PartialEventEquipmentRequestBuyableItem = {
  eventEquipmentRequest?: { id: string; event?: { id: string; name: string } | null } | null;
};

type EventEquipmentRequestBuyableItem = {
  eventEquipmentRequest: { id: string };
};

type EventEquipmentRequestWithEvent = {
  eventEquipmentRequest: { event: BuyableItemEvent };
};

export const EventEquipmentRequestBuyableItemHandler = {
  displayName: "Matériel",
  accounting: {
    generalAccountCode: "706112",
    invoiceLabel: "Matériel",
  },

  getCompleteItemName(item: PartialBuyableItem): string | null {
    const allocations = item.eventEquipmentRequest?.eventEquipmentAllocations ?? [];
    const equipmentNames = uniq(
      allocations.map((a) => a.eventEquipment?.name).filter((name): name is string => !!name),
    );
    if (equipmentNames.length === 0) {
      return null;
    }
    return `Matériel : ${equipmentNames.join(", ")}`;
  },

  getEvent(item: EventEquipmentRequestWithEvent): BuyableItemEvent {
    return item.eventEquipmentRequest.event;
  },

  isValid(item: PartialEventEquipmentRequestBuyableItem): boolean {
    return !!item.eventEquipmentRequest?.id;
  },

  findEquivalent(item: PartialEventEquipmentRequestBuyableItem): Prisma.BuyableItemWhereInput {
    return {
      eventEquipmentRequest: {
        id: item.eventEquipmentRequest?.id,
      },
    };
  },

  create({
    eventEquipmentRequest,
  }: EventEquipmentRequestBuyableItem): Omit<Prisma.BuyableItemCreateInput, "buyableType"> {
    return {
      eventEquipmentRequest: {
        connect: {
          id: eventEquipmentRequest.id,
        },
      },
    };
  },

  parseGroupKeyAsWhere(groupKey: string): Prisma.BuyableItemWhereInput {
    if (!EventEquipmentRequestBuyableItemHandler.isGroupKey(groupKey)) {
      throw new Error(`Invalid group key for event equipment request: ${groupKey}`);
    }

    const [, eventEquipmentRequestId] = groupKey.split("|");
    return {
      eventEquipmentRequest: {
        id: eventEquipmentRequestId,
      },
    };
  },

  isGroupKey(groupKey: string): boolean {
    return groupKey.startsWith(`${GROUP_KEY_PREFIX}|`);
  },

  getGroupKey(item: PartialEventEquipmentRequestBuyableItem): string {
    const requestId = item.eventEquipmentRequest?.id;
    if (!requestId) {
      throw new Error("Cannot get group key for invalid eventEquipmentRequest item");
    }
    return `${GROUP_KEY_PREFIX}|${requestId}`;
  },

  getPricingQuery(item: PartialBuyableItem): Prisma.PricingFindFirstArgs {
    return {
      where: {
        eventEquipmentRequest: {
          id: item.eventEquipmentRequest?.id,
        },
      },
    };
  },

  getLinkedGroupKeys(groupKey: string): string[] {
    const [, requestId] = groupKey.split("|");
    return [`${EQUIPMENT_DELIVERY_FEE_GROUP_KEY_PREFIX}|${requestId}`];
  },

  getEventSearchFilter(searchText: string): Prisma.BuyableItemWhereInput {
    return {
      eventEquipmentRequest: {
        event: { name: { contains: searchText, mode: "insensitive" } },
      },
    };
  },
} as const satisfies BuyableItemHandler;
